/* eslint-disable @typescript-eslint/no-explicit-any */
import { LayoutProps } from '@core/components/Layouts/Default/DefaultLayout';
import { MetaProps } from '@core/components/Meta/Meta';
import BlockList from '@core/components/Section/BlockList/BlockList';
import CompareTable from '@core/components/Section/CompareTable/CompareTable';
import Cta from '@core/components/Section/Cta/Cta';
import Faq from '@core/components/Section/Faq/Faq';
import Features from '@core/components/Section/Features/Features';
import Grid from '@core/components/Section/Grid/Grid';
import Hero from '@core/components/Section/Hero/Hero';
import Horizontal from '@core/components/Section/Horizontal/Horizontal';
import Investors from '@core/components/Section/Investors/Investors';
import Testimonials from '@core/components/Section/Testimonials/Testimonials';
import Vertical from '@core/components/Section/Vertical/Vertical';
import { useQuery } from '@tanstack/react-query';
import { CMS } from '@waitroom/models';
import { cmsPageQuery } from '@waitroom/react-query';
import { toBoolean } from '@waitroom/utils';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLoadScript } from '../../../modules/core/hooks/useLoadScript';
import Integrations from '../../session/components/Section/Integrations/Integrations';
import Pricing from '../../subscription/components/Section/Pricing/Pricing';
import { getImageUrl, getTextColor } from '../utils/base';

export interface UseCmsPageResponse {
  isLoading: boolean;
  meta?: MetaProps;
  page?: CMS.Page;
  ui: React.ReactElement | null;
  headerProps: LayoutProps['headerProps'];
}

const faScriptProps = {
  crossorigin: 'anonymous',
  async: true,
};

const sectionMap: Record<Exclude<CMS.Section['type'], undefined>, React.ComponentType<any>> = {
  vertical: Vertical,
  horizontal: Horizontal,
  hero: Hero,
  grid: Grid,
  testimonials: Testimonials,
  investors: Investors,
  cta: Cta,
  blockList: BlockList,
  pricing: Pricing,
  features: Features,
  faq: Faq,
  compareTable: CompareTable,
  integrations: Integrations,
};

export const useCmsPage = (slug?: string): UseCmsPageResponse => {
  const location = useLocation();
  const params = new URLSearchParams(location.search || window.location.search);
  const isPreview = toBoolean(params.get('preview'));
  // add font awesome
  useLoadScript('https://kit.fontawesome.com/3aec28c361.js', faScriptProps);
  const { data, isLoading } = useQuery(cmsPageQuery({ slug, isPreview }));
  const [page] = data?.data?.result || [];
  const { sections } = page || {};
  const img = page?.image?.asset?._ref;
  const ui = useMemo(() => {
    let prevPosition = 'right';
    if (!sections) return null;
    return (
      <>
        {sections.map((item, idx) => {
          const { section, background, connected } = item;
          const { type } = section || {};
          if (!section || !type) return null;
          const Component = sectionMap[type];
          if (!Component) return null;
          const itemProps = (section[type as keyof typeof section] || {}) as Dict;
          if (type === 'horizontal') {
            prevPosition = itemProps.position || prevPosition === 'left' ? 'right' : 'left';
            itemProps.position = itemProps.position || prevPosition;
          }
          const p = [20, 20, 28];
          return (
            <Component
              key={idx}
              data-index={idx}
              pt={connected ? 2 : p}
              pb={p}
              bgColor={background}
              color={getTextColor(background)}
              data-id={type}
              {...itemProps}
            />
          );
        })}
      </>
    );
  }, [sections]);

  const headerProps = useMemo<LayoutProps['headerProps']>(() => {
    const [firstSection] = sections || [];
    const { background } = firstSection || {};
    if (!background) return undefined;
    const [, value] = background.split('.');
    return {
      dark: value ? Number(value) > 500 : false,
      bgColor: background,
    };
  }, [sections]);

  return {
    isLoading,
    page,
    ui,
    headerProps,
    meta: page
      ? {
          title: page.title,
          description: page.description,
          image: img
            ? {
                url: getImageUrl(img, ['fit=crop', 'w=1280', 'h=720']) || '',
                size: [1280, 800],
              }
            : undefined,
        }
      : undefined,
  };
};
